import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { dimensions } from "../tokens/dimensions.js";

/**
 * PageHeader — the canonical page/screen header from the click-dummy.
 *
 * Title (+ optional muted subtitle) sit on the left, an optional cluster of
 * actions (buttons, filters, search) on the right. On narrow screens the two
 * groups wrap onto separate lines. This is the single sink for page titles:
 * the title renders as the `pageTitle` typography variant (16px/700) so every
 * page reads consistently — do not hand-roll page titles elsewhere.
 */
export default function PageHeader({ title, subtitle, actions, sx }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: { xs: "wrap", md: "nowrap" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: dimensions.xl,
        mb: "18px",
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {typeof title === "string" ? (
          <Typography variant="pageTitle" component="h1">
            {title}
          </Typography>
        ) : (
          title
        )}
        {subtitle &&
          (typeof subtitle === "string" ? (
            <Typography variant="pageSubtitle" sx={{ mt: "2px" }}>
              {subtitle}
            </Typography>
          ) : (
            subtitle
          ))}
      </Box>

      {actions && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: dimensions.md,
            flexShrink: 0,
            flexWrap: "wrap",
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
}

PageHeader.propTypes = {
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  actions: PropTypes.node,
  sx: PropTypes.object,
};
